import { renderGrid } from './grid.js';
import { t } from './i18n.js';
import { compressImage } from './images.js';
import { loadPhotos, savePhoto, deletePhoto } from './storage.js';
import { winningCells, progress } from './bingo.js';
import { playIntro } from './intro.js';

// read the data PHP placed in the page
const data = JSON.parse(document.getElementById('bingo-data').textContent);

// prefix for all stored photos of this card; another variant or version starts empty
const storagePrefix = `${data.card.slug}:v${data.card.version}:${data.variant}:`;

// everything that can change while the app is open
const state = {
    lang: data.lang,
    photos: new Map(),  // taskId -> object URL of the photo
    busy: new Set(),    // taskIds whose photo is being processed
    activeTaskId: null, // cell that opened the photo picker
    hasBingo: false,    // used to show the celebration only once per bingo
};

// look up elements once instead of on every render
const elements = {
    title: document.querySelector('[data-title]'),
    hint: document.querySelector('[data-hint]'),
    cardVariant: document.querySelector('[data-card-variant]'),
    grid: document.querySelector('[data-grid]'),
    langButtons: document.querySelectorAll('[data-lang]'),
    photoInput: document.querySelector('[data-photo-input]'),
    progress: document.querySelector('[data-progress]'),
    bingoDialog: document.querySelector('[data-bingo-dialog]'),
    bingoTitle: document.querySelector('[data-bingo-title]'),
    bingoText: document.querySelector('[data-bingo-text]'),
    bingoClose: document.querySelector('[data-bingo-close]'),
};

/**
 * Update the whole interface based on the current state.
 */
function render() {
    const { lang } = state;
    const rule = data.card.bingoRule;

    // keep <html lang> in sync: needed for hyphenation and screen readers
    document.documentElement.lang = lang;
    document.title = data.card.title[lang];

    elements.title.textContent = data.card.title[lang];
    elements.hint.textContent = t(lang, 'hint');
    elements.cardVariant.textContent = `${t(lang, 'Variant')} #${data.variant}`;
    elements.progress.textContent = t(lang, 'progress', progress(data.cells, state.photos));

    // dialog texts are set here too, so they follow the language switch
    elements.bingoTitle.textContent = t(lang, 'bingoTitle');
    elements.bingoText.textContent = t(lang, rule === 'line' ? 'bingoTextLine' : 'bingoTextFull');
    elements.bingoClose.textContent = t(lang, 'close');

    elements.langButtons.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.lang === lang));
    });

    renderGrid(elements.grid, data.cells, {
        lang,
        photos: state.photos,
        busy: state.busy,
        winning: winningCells(data.cells, state.photos, rule),
    });
}

/**
 * Switch language without reloading the page.
 */
function setLanguage(lang) {
    if (!data.card.languages.includes(lang) || lang === state.lang) {
        return;
    }

    state.lang = lang;

    // store the language in the URL, so a reload keeps it
    const url = new URL(window.location.href);

    url.searchParams.set('lang', lang);
    history.replaceState(null, '', url);

    render();
}

/**
 * Check for bingo and celebrate only when it is newly reached.
 * `celebrate` is false when restoring photos on page load, so a reload doesn't show the message again.
 */
function updateBingo({ celebrate }) {
    const hasBingo = winningCells(data.cells, state.photos, data.card.bingoRule).size > 0;

    if (celebrate && hasBingo && !state.hasBingo) {
        elements.bingoDialog.showModal();
        navigator.vibrate?.([100, 50, 100]); // short buzz on Android; ignored on iOS
    }

    state.hasBingo = hasBingo;
}

/**
 * Show a photo blob in a cell, or clear it when blob is null.
 * Object URLs keep the blob in memory, so old ones are revoked.
 */
function setPhoto(taskId, blob) {
    const previous = state.photos.get(taskId);

    if (previous) {
        URL.revokeObjectURL(previous);
    }

    if (blob) {
        state.photos.set(taskId, URL.createObjectURL(blob));
    } else {
        state.photos.delete(taskId);
    }
}

/**
 * Handle a newly picked photo: compress, show and store it.
 */
async function handlePhoto(taskId, file) {
    state.busy.add(taskId);
    render();

    try {
        const blob = await compressImage(file);

        setPhoto(taskId, blob);
        updateBingo({ celebrate: true });

        // if storage fails (e.g. private browsing), the photo still shows for this visit
        await savePhoto(storagePrefix + taskId, blob).catch((error) => {
            console.warn('Photo could not be stored', error);
        });
    } catch (error) {
        console.error(error);

        alert(t(state.lang, 'photoError'));
    } finally {
        state.busy.delete(taskId);
        render();
    }
}

async function removePhoto(taskId) {
    if (!confirm(t(state.lang, 'confirmRemove'))) {
        return;
    }

    setPhoto(taskId, null);
    updateBingo({ celebrate: false });
    render();

    await deletePhoto(storagePrefix + taskId).catch((error) => {
        console.warn('Photo could not be removed from storage', error);
    });
}

elements.langButtons.forEach((button) => {
    button.addEventListener('click', () => setLanguage(button.dataset.lang));
});

/**
 * Reject when a promise takes too long. Protects against storage that hangs
 * (rare, but it happens in some Safari versions), so the card always appears.
 */
function withTimeout(promise, ms) {
    return Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(new Error('Timed out')), ms)),
    ]);
}

/**
 * Show the card immediately, then add stored photos as soon as they are loaded.
 */
async function init() {
    try {
        const stored = await withTimeout(loadPhotos(storagePrefix), 1500);

        stored.forEach((blob, taskId) => setPhoto(taskId, blob));
    } catch (error) {
        console.warn('Storage unavailable', error);
    }

    updateBingo({ celebrate: false });
    render();
    playIntro(document.querySelector('.app'));

    // ask the browser not to clear our data when storage runs low
    navigator.storage?.persist?.().catch(() => {});
}

init();

// one listener on the grid instead of one per cell (event delegation)
elements.grid.addEventListener('click', (event) => {
    const target = event.target.closest('[data-action]');

    if (!target) {
        return;
    }

    const { action, taskId } = target.dataset;

    if (action === 'pick') {
        state.activeTaskId = taskId;
        elements.photoInput.click();
    }

    if (action === 'remove') {
        removePhoto(taskId);
    }
});

elements.photoInput.addEventListener('change', () => {
    const file = elements.photoInput.files[0];
    const taskId = state.activeTaskId;

    // reset, so picking the same file again still triggers a change event
    elements.photoInput.value = '';
    state.activeTaskId = null;

    if (file && taskId) {
        handlePhoto(taskId, file);
    }
});

// close the dialog when tapping the dark backdrop around it
elements.bingoDialog.addEventListener('click', (event) => {
    if (event.target === elements.bingoDialog) {
        elements.bingoDialog.close();
    }
});
